// @ts-check
import { actionHasReceivedVitalType } from './vital-type'
import { eventSelectors } from './events'
import { arrayAverage, createSelector } from '../utils/selector-utils'
import {
  coerceToCelsiusForStorage,
  displayTemperature,
} from '../utils/temperature-utils'

/**
 * @param {string} cpId Checkpoint ID
 * @param {string} vitalId Vital ID
 * @param {VitalValue} value Vital value
 * @returns {any} The changes to apply to the case.
 */
export const actionRecordVitalValue = (cpId, vitalId, value) => ({
  [`vitalRecords.${cpId}.vitals.${vitalId}.value`]:
    vitalId === 'temp' && value !== 'skipped'
      ? // @ts-ignore
        coerceToCelsiusForStorage(value)
      : value,
})

/**
 * @param {string} cpId Checkpoint ID
 * @param {{ [vitalId: string]: VitalValue }} values Vital values
 * @returns {any} The changes to apply to the case.
 */
export const actionRecordVitalValues = (cpId, values) => {
  let changes = {}
  for (const vitalId in values) {
    changes = {
      ...changes,
      ...actionRecordVitalValue(cpId, vitalId, values[vitalId]),
    }
  }
  return changes
}

/**
 * @param {string} cpId Checkpoint ID
 * @param {string} vitalId Vital ID
 * @returns {any} The changes to apply to the case.
 */
export const actionSkipVitalValue = (cpId, vitalId) =>
  actionRecordVitalValue(cpId, vitalId, 'skipped')

/**
 * @param {string} cpId Checkpoint ID
 * @param {EmittableVitalType} vitalId Vital ID
 * @param {AutoVitalEntry} vitalData Vital entry
 * @returns {any} The changes to apply to the case.
 */
export const actionRecordAutoVitalValue = (cpId, vitalId, vitalData) => {
  let toSave = vitalData
  if (vitalId === 'temp') {
    toSave = {
      ...toSave,
      // @ts-ignore
      value: coerceToCelsiusForStorage(vitalData.value),
    }
  }
  return {
    [`autoVitalRecords.${cpId}.vitals.${vitalId}`]: toSave,
    // We also note that we have received this
    // vital type from a monitor.
    ...actionHasReceivedVitalType(vitalId),
  }
}

/**
 * @param {string} cpId Checkpoint ID
 * @param {{ [vitalId: string]: AutoVitalEntry }} values Vital value
 * @returns {any} The changes to apply to the case.
 */
export const actionRecordAutoVitalValues = (cpId, values) => {
  let changes = {}

  for (const vitalId in values) {
    changes = {
      ...changes,
      // @ts-ignore
      ...actionRecordAutoVitalValue(cpId, vitalId, values[vitalId]),
    }
  }
  return changes
}

const empty = {}
const selectVitalRecordsRaw = caseData => caseData.vitalRecords || empty
const selectAutoVitalRecordsRaw = caseData => caseData.autoVitalRecords || empty
const selectVitalRecords = createSelector(selectVitalRecordsRaw, records => {
  const toReturn = {}
  for (const checkpointId in records) {
    toReturn[checkpointId] = {}
    const vitals = records[checkpointId].vitals
    for (const vitalId in vitals) {
      toReturn[checkpointId][vitalId] = vitals[vitalId].value
    }
  }
  return toReturn
})
const selectVitalAutoRecords = createSelector(
  selectAutoVitalRecordsRaw,
  records => {
    const toReturn = {}
    for (const checkpointId in records) {
      toReturn[checkpointId] = records[checkpointId].vitals
    }
    return toReturn
  }
)
const addVital = (acc, cpId, vitalType, value) => {
  if (value > 0 && value < 250) {
    const res = acc[cpId] || (acc[cpId] = {})
    res[vitalType] = Number(value)
  }
}

export const vitalsSelectors = {
  selectVitalRecordsRaw,
  selectVitalRecords,
  selectAutoVitalRecordsRaw,
  selectVitalAutoRecords,
  selectEstimatedBreathsBeats: createSelector(
    selectVitalRecords,
    selectVitalAutoRecords,
    eventSelectors.selectMonitoringMinutes,
    (records, autoRecords, minutes) => {
      const byCheckpoint = {}
      for (const cpId in autoRecords) {
        const entry = autoRecords[cpId]
        addVital(byCheckpoint, cpId, 'rr', entry.rr && entry.rr.value)
        addVital(byCheckpoint, cpId, 'hr', entry.hr && entry.hr.value)
      }
      for (const cpId in records) {
        const entry = records[cpId]
        addVital(byCheckpoint, cpId, 'rr', entry.rr)
        addVital(byCheckpoint, cpId, 'hr', entry.hr)
      }
      const rr = []
      const hr = []
      for (const entries of Object.entries(byCheckpoint)) {
        const value = entries[1]
        if (value.rr) rr.push(value.rr)
        if (value.hr) hr.push(value.hr)
      }
      const toReturn = { breaths: 0, heartbeats: 0 }
      if (minutes) {
        const avgRR = arrayAverage(rr)
        const avgHR = arrayAverage(hr)
        if (avgRR) {
          toReturn.breaths = Math.round(avgRR * minutes)
        }
        if (avgHR) {
          toReturn.heartbeats = Math.round(avgHR * minutes)
        }
      }
      return toReturn
    }
  ),
  // these two accept options and are not directly memoized.
  // They're expected to be used as a part of a larger
  // selector that is memoized.
  optionSelectVitalRecords:
    /**
     * @param {CaseData} caseData
     * @param {{ tempUnit: TempUnit }} param1
     * @returns {{
     *   [checkpointId: string]: {
     *     [vitalId: string]: VitalValue
     *   }
     * }}
     */
    (caseData, { tempUnit }) => {
      const records = selectVitalRecords(caseData)
      /**
       * @type {{
       *   [checkpointId: string]: {
       *     [vitalId: string]: VitalValue
       *   }
       * }}
       */
      const toReturn = {}
      for (const checkpointId in records) {
        toReturn[checkpointId] = {}
        const vitals = records[checkpointId]
        for (const vitalId in vitals) {
          let value = vitals[vitalId]
          if (vitalId === 'temp') {
            value = displayTemperature(value, tempUnit)
          }
          toReturn[checkpointId][vitalId] = value
        }
      }
      return toReturn
    },
  optionSelectVitalAutoRecords:
    /**
     * @param {CaseData} caseData
     * @param {{ tempUnit: TempUnit }} param1
     * @returns {{
     *   [checkpointId: string]: {
     *     [vitalId: string]: AutoVitalEntry
     *   }
     * }}
     */
    (caseData, { tempUnit }) => {
      const records = selectAutoVitalRecordsRaw(caseData)
      /**
       * @type {{
       *   [checkpointId: string]: {
       *     [vitalId: string]: AutoVitalEntry
       *   }
       * }}
       */
      const toReturn = {}
      for (const checkpointId in records) {
        const vitals = records[checkpointId].vitals
        toReturn[checkpointId] = {}
        for (const vitalId in vitals) {
          let entry = vitals[vitalId]
          if (vitalId === 'temp') {
            const coerced = displayTemperature(entry.value, tempUnit)
            if (coerced !== entry.value) {
              entry = {
                ...entry,
                value: coerced,
              }
            }
          }
          toReturn[checkpointId][vitalId] = entry
        }
      }
      return toReturn
    },
}
